import moment from "moment";
import { DATE_FORMATS } from "../../../../constants";

const { DATE_YEAR_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

/**
 * Convert number date (YYYYMMDD) to moment object
 * @param {number} dateNum - Date as number (e.g., 20251014)
 * @returns {moment} - Moment object
 */
const convertDateNumToMoment = (dateNum) => {
  const dateStr = String(dateNum);
  return moment(dateStr, "YYYYMMDD");
};

const DEFAULT_PERIOD_DURATION_DAYS = 5;

/**
 * Get effective end date for a log that has no endDate: the earlier of
 * (startDate + periodDuration - 1) or today (so we don't extend into the future).
 * @param {number} startDateNum - Start date as YYYYMMDD number
 * @param {number} periodDuration - Period duration in days
 * @returns {number} - Effective end date as YYYYMMDD number
 */
export const getEffectiveEndDate = (startDateNum, periodDuration) => {
  const start = convertDateNumToMoment(startDateNum);
  const endByDuration = start.clone().add(periodDuration - 1, "days");
  const today = moment().startOf("day");
  const effectiveEnd =
    endByDuration.isBefore(today) || endByDuration.isSame(today)
      ? endByDuration
      : today;
  return Number(effectiveEnd.format("YYYYMMDD"));
};

/**
 * Build initial log from female cycle settings (first registered period).
 * @param {Object} settings - Female cycle settings
 * @returns {Object|null} - Log with startDate/endDate (YYYYMMDD numbers) or null
 */
const buildInitialLogFromSettings = (settings) => {
  if (!settings?.firstRegisteredPeriod) return null;

  const start = moment(settings.firstRegisteredPeriod, "YYYYMMDD");
  const end = start.clone().add(settings.averagePeriodLength - 1, "days");

  return {
    startDate: Number(start.format("YYYYMMDD")),
    endDate: Number(end.format("YYYYMMDD")),
    source: "settings",
  };
};

/**
 * Merge femaleCycleLogs with settings-derived log and sort by startDate descending.
 * @param {Array} femaleCycleLogs - Array of female cycle logs
 * @param {Object} femaleCycleSettings - Female cycle settings
 * @returns {Array} - Normalized logs sorted by startDate descending
 */
export const getNormalizedLogs = (femaleCycleLogs, femaleCycleSettings) => {
  const baseLogs = femaleCycleLogs ?? [];
  const settingsLog = buildInitialLogFromSettings(femaleCycleSettings);

  if (!settingsLog) {
    return [...baseLogs].sort((a, b) => b.startDate - a.startDate);
  }

  const alreadyExists = baseLogs.some(
    (log) => log?.startDate === settingsLog?.startDate
  );
  const merged = alreadyExists ? baseLogs : [settingsLog, ...baseLogs];
  return [...merged].sort((a, b) => b.startDate - a.startDate);
};

/**
 * Expand normalized period logs into per-day entries (logged only).
 * @param {Array} normalizedLogs - Logs from getNormalizedLogs
 * @param {string} dateFormat - Moment format for date keys
 * @returns {Array<{ date: string, dayNumber: number, type: "logged" }>}
 */
export const expandLoggedFemaleCycleDays = (
  normalizedLogs,
  dateFormat = DATE_DAY_FORMAT
) => {
  const expanded = [];
  for (const log of normalizedLogs) {
    if (!log.startDate) continue;
    const start = moment.utc(String(log.startDate), "YYYYMMDD");
    const end = log.endDate
      ? moment.utc(String(log.endDate), "YYYYMMDD")
      : start.clone().add(4, "days");
    const d = start.clone();
    let dayNum = 1;
    while (d.isSameOrBefore(end)) {
      expanded.push({
        date: d.format(dateFormat),
        dayNumber: dayNum,
        type: "logged",
      });
      d.add(1, "day");
      dayNum++;
    }
  }
  return expanded;
};

/**
 * Calculate average cycle length from logs
 * @param {Array} logs - Array of female cycle logs
 * @returns {number} - Average cycle length in days
 */
export const calculateAverageCycleLength = (logs) => {
  if (!logs || logs.length < 2) return null;

  // Sort logs by startDate
  const sortedLogs = [...logs].sort((a, b) => a.startDate - b.startDate);

  // Get all period start dates
  const periodStarts = sortedLogs.map((log) =>
    convertDateNumToMoment(log.startDate)
  );

  if (periodStarts.length < 2) return null;

  // Calculate differences between consecutive period starts
  const cycleLengths = [];
  for (let i = 1; i < periodStarts.length; i++) {
    const diff = periodStarts[i].diff(periodStarts[i - 1], "days");
    // Only consider reasonable cycle lengths (21-45 days)
    if (diff >= 21 && diff <= 45) {
      cycleLengths.push(diff);
    }
  }

  if (cycleLengths.length === 0) return null;

  return Math.round(
    cycleLengths.reduce((sum, len) => sum + len, 0) / cycleLengths.length
  );
};

/**
 * Calculate average period duration from logs.
 * For logs without endDate, uses effective end = min(startDate + defaultPeriodDuration - 1, today).
 * @param {Array} logs - Array of female cycle logs
 * @param {number} defaultPeriodDuration - Default period duration in days when log has no endDate
 * @returns {number} - Average period duration in days
 */
export const calculateAveragePeriodDuration = (
  logs,
  defaultPeriodDuration = DEFAULT_PERIOD_DURATION_DAYS
) => {
  if (!logs || logs.length === 0) return null;

  const durations = [];
  for (const log of logs) {
    if (!log?.startDate) continue;
    if (log.endDate) {
      const start = convertDateNumToMoment(log.startDate);
      const end = convertDateNumToMoment(log.endDate);
      durations.push(end.diff(start, "days") + 1);
    } else {
      const effectiveEnd = getEffectiveEndDate(
        log.startDate,
        defaultPeriodDuration
      );
      const start = convertDateNumToMoment(log.startDate);
      const end = convertDateNumToMoment(effectiveEnd);
      durations.push(end.diff(start, "days") + 1);
    }
  }

  if (durations.length === 0) return null;

  return Math.round(
    durations.reduce((sum, dur) => sum + dur, 0) / durations.length
  );
};

/**
 * Get the last logged period start date
 * @param {Array} logs - Array of female cycle logs
 * @returns {moment} - Last period start date
 */
export const getLastPeriodStart = (logs) => {
  if (!logs || logs.length === 0) return null;

  // Sort logs by startDate (descending)
  const sortedLogs = [...logs].sort((a, b) => b.startDate - a.startDate);

  // Return the most recent log's start date
  return convertDateNumToMoment(sortedLogs[0].startDate);
};

/**
 * Whether settings contain usable period/cycle data for predictions.
 * @param {Object} settings - Female cycle settings
 * @returns {boolean}
 */
export const hasPeriodSettings = (settings) =>
  Boolean(
    settings &&
      (settings.averageCycleLength != null ||
        settings.averagePeriodLength != null ||
        settings.firstRegisteredPeriod != null)
  );

/**
 * Generate predicted cycles for the next N months.
 * Only returns predictions when period settings exist and (when no logs) we do not guess from "today".
 *
 * @param {Array} logs - Array of female cycle logs
 * @param {Object} settings - Female cycle settings
 * @param {moment} startMonth - Starting month for predictions
 * @param {number} numberOfMonths - Number of months to predict
 * @returns {Array} - Array of predicted period objects
 */
export const getPredictedCycles = (
  logs,
  settings,
  startMonth,
  numberOfMonths = 5
) => {
  if (!hasPeriodSettings(settings)) return [];

  const predictions = [];

  // Get cycle parameters (pass default so logs without endDate use effective end)
  const defaultPeriodDuration = settings?.averagePeriodLength ?? 5;
  let avgCycleLength = calculateAverageCycleLength(logs);
  let avgPeriodDuration = calculateAveragePeriodDuration(
    logs,
    defaultPeriodDuration
  );

  // Fallback to settings if no logs available
  if (!avgCycleLength) {
    avgCycleLength = settings?.averageCycleLength ?? 28;
  }
  if (!avgPeriodDuration) {
    avgPeriodDuration = defaultPeriodDuration;
  }

  // Get last period start: require at least one log so we don't predict from "today" only
  const lastPeriodStart = getLastPeriodStart(logs);
  if (!lastPeriodStart) {
    return predictions;
  }

  // Calculate predictions based on last period
  const endOfPredictionRange = moment(startMonth, DATE_YEAR_FORMAT)
    .add(numberOfMonths, "months")
    .endOf("month");

  let cycleCount = 1;
  let predictedStart = lastPeriodStart.clone().add(avgCycleLength, "days");

  // Generate predictions until we reach the end of the prediction range
  while (predictedStart.isSameOrBefore(endOfPredictionRange)) {
    const predictedEnd = predictedStart
      .clone()
      .add(avgPeriodDuration - 1, "days");

    predictions.push({
      startDate: predictedStart.format(DATE_DAY_FORMAT),
      endDate: predictedEnd.format(DATE_DAY_FORMAT),
      isPrediction: true,
    });

    cycleCount++;
    predictedStart = lastPeriodStart
      .clone()
      .add(cycleCount * avgCycleLength, "days");
  }

  return predictions;
};

/**
 * Get list of months that have logs
 * @param {Array} logs - Array of female cycle logs
 * @returns {Array} - Array of month strings in DATE_YEAR_FORMAT
 */
export const getAvailableMonths = (logs) => {
  if (!logs || logs.length === 0) return [];

  const months = new Set();
  logs.forEach((log) => {
    const monthKey = convertDateNumToMoment(log.startDate).format(
      DATE_YEAR_FORMAT
    );
    months.add(monthKey);
  });

  return Array.from(months).sort();
};

/**
 * Get the earliest month with logs
 * @param {Array} logs - Array of female cycle logs
 * @returns {string|null} - Earliest month in DATE_YEAR_FORMAT
 */
export const getEarliestMonth = (logs) => {
  const availableMonths = getAvailableMonths(logs);
  return availableMonths.length > 0 ? availableMonths[0] : null;
};

/**
 * Check if navigation to a specific month is allowed
 * @param {string} targetMonth - Month to navigate to
 * @param {Array} availableMonths - Array of months with logs
 * @returns {boolean} - Whether navigation is allowed
 */
export const canNavigateToMonth = (targetMonth, availableMonths) => {
  if (availableMonths.length === 0) {
    // No logs yet, only allow current month
    const today = moment().format(DATE_YEAR_FORMAT);
    return targetMonth === today;
  }

  const target = moment(targetMonth, DATE_YEAR_FORMAT);
  const earliest = moment(availableMonths[0], DATE_YEAR_FORMAT);
  const today = moment().startOf("month");

  // Can navigate if:
  // 1. Target month has logs (is in availableMonths)
  // 2. Target month is between earliest log and today (inclusive)
  const hasLogs = availableMonths.includes(targetMonth);
  const isInRange =
    target.isSameOrAfter(earliest) && target.isSameOrBefore(today);

  return hasLogs || isInRange;
};

/**
 * Get the next N months from a given month
 * @param {string} currentMonth - Starting month
 * @param {number} count - Number of months to get
 * @returns {Array} - Array of month strings in DATE_YEAR_FORMAT
 */
export const getNextMonths = (currentMonth, count = 5) => {
  const months = [];
  for (let i = 1; i <= count; i++) {
    months.push(
      moment(currentMonth, DATE_YEAR_FORMAT)
        .add(i, "months")
        .format(DATE_YEAR_FORMAT)
    );
  }
  return months;
};

/**
 * Check if a specific month has logs
 * @param {Array} logs - Array of female cycle logs
 * @param {string} month - Month to check in DATE_YEAR_FORMAT
 * @returns {boolean} - Whether the month has logs
 */
export const hasLogForMonth = (logs, month) => {
  if (!logs || logs.length === 0) return false;

  return logs.some((log) => {
    const logMonth = convertDateNumToMoment(log.startDate).format(
      DATE_YEAR_FORMAT
    );
    return logMonth === month;
  });
};

/**
 * Get the next unregistered month (first month from today with no logs).
 * Used to show predicted cycle for only the next month that has no data yet.
 * @param {Array} logs - Array of female cycle logs
 * @returns {string|null} - Month in DATE_YEAR_FORMAT or null
 */
export const getNextUnregisteredMonth = (logs) => {
  const availableMonths = getAvailableMonths(logs || []);
  let current = moment().startOf("month");

  // Only consider up to 12 months ahead to avoid infinite loop
  for (let i = 0; i < 12; i++) {
    const monthKey = current.format(DATE_YEAR_FORMAT);
    if (!availableMonths.includes(monthKey)) {
      return monthKey;
    }
    current = current.clone().add(1, "month");
  }
  return null;
};

/**
 * Format logs and predictions for calendar display
 * @param {Array} logs - Array of female cycle logs
 * @param {Array} predictions - Array of predicted periods
 * @param {string} selectedMonth - Selected month in DATE_YEAR_FORMAT
 * @param {number} defaultPeriodDuration - Default period duration in days when log has no endDate
 * @returns {Object} - Calendar data organized by month
 */
export const formatLogsForCalendar = (
  logs,
  predictions,
  selectedMonth,
  defaultPeriodDuration = DEFAULT_PERIOD_DURATION_DAYS
) => {
  // Generate months to show from current to 5 months ahead
  const currentMonth = moment(selectedMonth, DATE_YEAR_FORMAT);
  const monthsToShow = [currentMonth.format(DATE_YEAR_FORMAT)];
  for (let i = 1; i <= 5; i++) {
    monthsToShow.push(
      moment(currentMonth, DATE_YEAR_FORMAT)
        .add(i, "months")
        .format(DATE_YEAR_FORMAT)
    );
  }

  const calendarData = {};

  // Initialize all months
  monthsToShow.forEach((month) => {
    calendarData[month] = {
      periodDays: [],
      predictedPeriodDays: [],
      symptoms: [],
      intensityFlow: null,
      logs: [],
    };
  });

  const avgDuration =
    calculateAveragePeriodDuration(logs, defaultPeriodDuration) ??
    defaultPeriodDuration;

  // Add logged data
  if (logs && logs.length > 0) {
    logs.forEach((log) => {
      const startMoment = convertDateNumToMoment(log.startDate);
      const endMoment = log.endDate
        ? convertDateNumToMoment(log.endDate)
        : convertDateNumToMoment(
            getEffectiveEndDate(log.startDate, avgDuration)
          );

      // Track which months this log spans
      const monthsInLog = new Set();

      // Generate all days in the period range
      let currentDay = startMoment.clone();
      while (currentDay.isSameOrBefore(endMoment)) {
        const month = currentDay.format(DATE_YEAR_FORMAT);
        const dateStr = currentDay.format(DATE_DAY_FORMAT);

        if (calendarData[month]) {
          // Add to period days
          if (!calendarData[month].periodDays.includes(dateStr)) {
            calendarData[month].periodDays.push(dateStr);
          }
          monthsInLog.add(month);
        }

        currentDay = currentDay.add(1, "day");
      }

      // Store the log reference and aggregate symptoms/flowIntensity for each month the log spans
      monthsInLog.forEach((month) => {
        if (
          calendarData[month] &&
          !calendarData[month].logs.some((l) => l._id === log._id)
        ) {
          calendarData[month].logs.push(log);

          // Add symptoms if they exist (collect unique symptoms)
          if (log.symptoms && log.symptoms.length > 0) {
            log.symptoms.forEach((symptom) => {
              if (!calendarData[month].symptoms.includes(symptom)) {
                calendarData[month].symptoms.push(symptom);
              }
            });
          }

          // Set intensityFlow if it exists (use the most recent log's flowIntensity)
          if (log.flowIntensity) {
            calendarData[month].intensityFlow = log.flowIntensity;
          }
        }
      });
    });
  }

  // Add predictions
  if (predictions && predictions.length > 0) {
    predictions.forEach((prediction) => {
      const start = moment(prediction.startDate);
      const end = moment(prediction.endDate);

      // Generate all days in the prediction range
      let currentDay = start.clone();
      while (currentDay.isSameOrBefore(end)) {
        const month = currentDay.format(DATE_YEAR_FORMAT);
        const dateStr = currentDay.format(DATE_DAY_FORMAT);

        if (calendarData[month]) {
          // Only add as prediction if not already logged
          const isLogged = calendarData[month].periodDays.includes(dateStr);
          if (!isLogged) {
            calendarData[month].predictedPeriodDays.push(dateStr);
          }
        }

        currentDay = currentDay.add(1, "day");
      }
    });
  }

  return calendarData;
};

/**
 * Check if user can navigate backwards (to previous month)
 * @param {string} currentMonth - Current selected month
 * @param {Array} availableMonths - Months with logs
 * @returns {boolean} - Whether backward navigation is allowed
 */
export const canNavigateBackward = (currentMonth, availableMonths) => {
  if (availableMonths.length === 0) return false;

  const current = moment(currentMonth, DATE_YEAR_FORMAT);
  const previousMonth = current.clone().subtract(1, "month");

  return canNavigateToMonth(
    previousMonth.format(DATE_YEAR_FORMAT),
    availableMonths
  );
};

/**
 * Check if user can navigate forwards (to next month)
 * @param {string} currentMonth - Current selected month
 * @param {Array} availableMonths - Months with logs
 * @returns {boolean} - Whether forward navigation is allowed
 */
export const canNavigateForward = (currentMonth, availableMonths) => {
  const current = moment(currentMonth, DATE_YEAR_FORMAT);
  const today = moment().startOf("month");
  const nextMonth = current.clone().add(1, "month");

  // Can't go beyond current month
  if (nextMonth.isAfter(today)) return false;

  return canNavigateToMonth(
    nextMonth.format(DATE_YEAR_FORMAT),
    availableMonths
  );
};
